/**
 * Mirror of ball_worker detection_mask.compute_detection_roi (normalized).
 * Union AABB of table corners + player boxes + margin (fraction of frame).
 */
export function computeDetectionRoiNorm({
  cornersNorm = null,
  playerRois = null,
  margin = 0.05,
} = {}) {
  const xs = [];
  const ys = [];

  if (Array.isArray(cornersNorm) && cornersNorm.length === 4) {
    for (const pt of cornersNorm) {
      if (!pt || typeof pt !== "object") continue;
      const x = Number(pt.x);
      const y = Number(pt.y);
      if (Number.isFinite(x) && Number.isFinite(y)) {
        xs.push(x);
        ys.push(y);
      }
    }
  }

  for (const key of ["left", "right"]) {
    const roi = playerRois?.[key];
    if (!roi) continue;
    const x = Number(roi.x);
    const y = Number(roi.y);
    const w = Number(roi.w ?? roi.width);
    const h = Number(roi.h ?? roi.height);
    if (![x, y, w, h].every((n) => Number.isFinite(n)) || w <= 0 || h <= 0) {
      continue;
    }
    xs.push(x, x + w);
    ys.push(y, y + h);
  }

  if (!xs.length || !ys.length) return null;

  const pad = Number.isFinite(margin) ? Math.max(0, margin) : 0.05;
  const x0 = Math.max(0, Math.min(...xs) - pad);
  const y0 = Math.max(0, Math.min(...ys) - pad);
  const x1 = Math.min(1, Math.max(...xs) + pad);
  const y1 = Math.min(1, Math.max(...ys) + pad);
  const w = x1 - x0;
  const h = y1 - y0;
  if (w < 0.04 || h < 0.04) return null;
  return {
    x: round5(x0),
    y: round5(y0),
    w: round5(w),
    h: round5(h),
  };
}

/**
 * Normalize the ROI stored on a ball job (`settings.detection_roi`).
 * Accepts {norm:{x,y,w,h}}, {x,y,w,h}, or pixel {x0,y0,x1,y1,width,height}.
 */
export function normalizeStoredDetectionRoi(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (raw.norm && typeof raw.norm === "object") {
    return clampDetectionRoiNorm(raw.norm);
  }
  if (raw.x != null && (raw.w != null || raw.width != null)) {
    return clampDetectionRoiNorm(raw);
  }
  if (raw.x0 != null && raw.x1 != null && raw.y0 != null && raw.y1 != null) {
    const x0 = Number(raw.x0);
    const y0 = Number(raw.y0);
    const x1 = Number(raw.x1);
    const y1 = Number(raw.y1);
    const fw = Number(raw.width);
    const fh = Number(raw.height);
    if (![x0, y0, x1, y1].every((n) => Number.isFinite(n)) || x1 <= x0 || y1 <= y0) {
      return null;
    }
    if (fw > 1 && fh > 1) {
      return clampDetectionRoiNorm({
        x: x0 / fw,
        y: y0 / fh,
        w: (x1 - x0) / fw,
        h: (y1 - y0) / fh,
      });
    }
    if (x1 <= 1.0001 && y1 <= 1.0001) {
      return clampDetectionRoiNorm({
        x: x0,
        y: y0,
        w: x1 - x0,
        h: y1 - y0,
      });
    }
  }
  return null;
}

export function clampDetectionRoiNorm(roi) {
  if (!roi || typeof roi !== "object") return null;
  const x = Number(roi.x);
  const y = Number(roi.y);
  const w = Number(roi.w ?? roi.width);
  const h = Number(roi.h ?? roi.height);
  if (![x, y, w, h].every((n) => Number.isFinite(n)) || w <= 0 || h <= 0) {
    return null;
  }
  const cx = Math.min(1, Math.max(0, x));
  const cy = Math.min(1, Math.max(0, y));
  const cw = Math.min(1 - cx, Math.max(0, w));
  const ch = Math.min(1 - cy, Math.max(0, h));
  if (cw < 0.04 || ch < 0.04) return null;
  return { x: round5(cx), y: round5(cy), w: round5(cw), h: round5(ch) };
}

function round5(n) {
  return Math.round(n * 1e5) / 1e5;
}
